/**
 * EventTitleSponsor — top billing, under the hero.
 *
 * Draws the level-1 sponsors: the ones the organizer marked as top billing,
 * whatever they are called. On an event with a title sponsor that is the
 * title sponsor; on an event without one — which is every AthloHive event
 * today — it is whoever was marked level 1, typically "Powered By". The
 * caption shown is the sponsor's own, so the strip never claims a billing
 * nobody bought.
 *
 * Beside them, a button that scrolls to the Sponsors section.
 *
 * That button used to open a marquee of the other sponsors through this same
 * strip, and it was wrong twice over. It put the partners inside the title
 * sponsor's own panel, where they read as equals and diluted the one thing
 * that sponsor paid for; and it was a second, worse rendering of a list the
 * Sponsors section already shows grouped and captioned in full. Scrolling
 * keeps one copy of that list, costs no height here, and leaves the strip to
 * the sponsor who bought it.
 *
 * The button counts what it is sending you to — "1 partner", "5 partners" —
 * rather than saying "Partners" over a section that might hold one logo.
 * A visitor decides whether a scroll is worth it from that number, and it is
 * the difference between an honest control and a teaser.
 *
 * It renders nothing — no card, no heading, no gap — when nobody is level 1.
 * That is every event written before these columns existed, so it is the
 * case that has to be right.
 */

import React from 'react';
import { parseLevel } from '../sponsorHeadings';

const TOP_LEVEL = 1;

/** The id EventSponsors puts on its section, and this button scrolls to. */
export const SPONSORS_SECTION_ID = 'event-sponsors';

/** How long the arrival highlight stays on the section, in ms. */
const ARRIVAL_MS = 1600;

export default function EventTitleSponsor({ sponsors, resolveUrl }) {
  const items = Array.isArray(sponsors) ? sponsors : [];
  /*
    Exactly one sponsor gets the strip.

    validateEventForm refuses a second level-1 sponsor, so this is normally a
    list of one. It is enforced here too because the public page also renders
    rows written before that rule, or set in Django Admin, and a strip of two
    cannot be drawn honestly: it carries one caption, so the second sponsor
    appears under a heading that is not theirs. The one shown is whichever the
    organizer placed highest; the rest keep their place in the Sponsors
    section, so nobody is dropped from the page.
  */
  const topBilled = items
    .filter((s) => parseLevel(s?.sponsor_level) === TOP_LEVEL)
    .sort((a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0));
  const top = topBilled.slice(0, 1);
  const others = items.length - top.length;

  // Guarded here as well as at the call site: a later edit that drops the
  // check in the template still cannot render an empty shell.
  if (!top.length) return null;

  // One caption for the strip, taken from the highest-placed level-1
  // sponsor. Captioning each logo separately was tried and dropped: on a
  // narrow column the captions wrap to two lines each and the strip grows
  // taller than the facts panel below it, for wording the Sponsors section
  // already carries in full.
  const caption = String(top[0]?.sponsor_label || '').trim();

  const goToSponsors = () => {
    const section = document.getElementById(SPONSORS_SECTION_ID);
    if (!section) return;
    // A visitor who asked the browser for less motion gets the jump, not the
    // glide. Same destination either way.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    // Focus follows the scroll, or a keyboard visitor is moved down the page
    // while their place in the tab order stays behind. preventScroll because
    // the line above already owns the movement.
    section.focus?.({ preventScroll: true });
    // A page that silently slides somewhere leaves people hunting for what
    // changed. The section says "here" for a moment and then settles.
    section.classList.add('is-arrived');
    window.setTimeout(() => section.classList.remove('is-arrived'), ARRIVAL_MS);
  };

  return (
    <section className="evb-tsponsor" aria-label={caption || 'Sponsors'}>
      {caption && (
        <p className="evb-tsponsor__rule"><span>{caption}</span></p>
      )}

      <div className="evb-tsponsor__body">
        <div className="evb-tsponsor__pinned">
          {top.map((sponsor, index) => {
            const logoUrl = resolveUrl(sponsor?.logo_url);
            const name = sponsor?.name;
            return (
              <a
                className="evb-tsponsor__item"
                key={sponsor?.id ?? index}
                href={resolveUrl(sponsor?.website_url) || undefined}
              >
                {logoUrl && (
                  <img className="evb-tsponsor__logo" src={logoUrl} alt="" />
                )}
                {/* The name is printed, not left as alt text. A wordmark a
                    visitor cannot read is a sponsor they cannot name, and the
                    logo is often a symbol rather than the company's name. It
                    truncates rather than wrapping, so the strip keeps its
                    height whatever the sponsor is called; the full name stays
                    in the tooltip. */}
                <span className="evb-tsponsor__name" title={name}>{name}</span>
              </a>
            );
          })}
        </div>

        {/* No other sponsors, no button: it would scroll to a section holding
            only the sponsor already shown here. */}
        {others > 0 && (
          <button
            type="button"
            className="evb-tsponsor__btn"
            onClick={goToSponsors}
            aria-controls={SPONSORS_SECTION_ID}
          >
            {others} {others === 1 ? 'partner' : 'partners'}
            <span aria-hidden="true">↓</span>
          </button>
        )}
      </div>
    </section>
  );
}
